'use client'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { cx } from '@/lib/utils'

import { useAccordion } from './useAccordion'

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

interface AccordionItemProps {
  header?: string
  index: number
  headingTag: HeadingLevel
  children: ReactNode
  isFirst?: boolean
  hasTitle?: boolean
  editableProps?: Record<string, unknown>
}

const ChevronIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)

export const AccordionItem = ({
  header,
  index,
  headingTag: HeadingTag,
  children,
  isFirst,
  hasTitle,
  editableProps,
}: AccordionItemProps) => {
  const context = useAccordion()
  const bodyRef = useRef<HTMLDivElement>(null)
  const isActive = context.currentIndex === index
  const [height, setHeight] = useState<string>(isActive ? 'auto' : '0')

  useEffect(() => {
    if (!bodyRef.current) return
    requestAnimationFrame(() => {
      if (!bodyRef.current) return
      const bodyHeight = isActive ? bodyRef.current.scrollHeight : 0
      setHeight(`${bodyHeight}px`)
    })
  }, [isActive])

  const toggle = () => context.setCurrentIndex(isActive ? undefined : index)

  return (
    <section
      {...editableProps}
      className={cx(
        'flex w-full items-start gap-4 border-b border-slate-200 py-5 transition-colors duration-200 last:border-b-0 last:pb-0',
        isFirst && !hasTitle && 'pt-0',
        isFirst && hasTitle && 'border-t border-t-slate-200',
        isActive && 'border-slate-300'
      )}
    >
      <section className="w-full">
        <HeadingTag className="m-0! flex max-w-none! flex-col">
          <button
            type="button"
            data-tracking="ignore"
            className="text-navy flex min-h-[32px] w-full cursor-pointer items-center justify-between border-none text-left text-[1.225rem] leading-[1.5] font-semibold tracking-normal transition-colors duration-150 ease-in-out hover:opacity-70"
            onClick={toggle}
            aria-expanded={isActive}
          >
            <span className="pr-4">{header}</span>
            <ChevronIcon
              className={cx(
                'shrink-0 text-slate-400 transition-transform duration-300 ease-out',
                isActive && 'text-navy rotate-180'
              )}
            />
          </button>
        </HeadingTag>
        <section
          ref={bodyRef}
          data-ht-disclosure-content
          inert={!isActive}
          aria-hidden={!isActive}
          style={{ maxHeight: isActive ? height : '0' }}
          className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        >
          <section className="prose-body-text pt-3 pb-1 [&_li]:mb-1.5 [&_li]:text-[clamp(18px,0.9rem+0.4vw,1.2rem)] [&_li]:leading-[1.7] [&_ol]:my-3 [&_ol]:ml-6 [&_ol_li]:list-decimal [&_p]:m-0 [&_p]:text-[clamp(18px,0.9rem+0.4vw,1.2rem)] [&_p]:leading-[1.7] [&_p+p]:mt-3 [&_ul]:my-3 [&_ul]:ml-6 [&_ul_li]:list-disc">
            {children}
          </section>
        </section>
      </section>
    </section>
  )
}
